import { forwardRef, Fragment, useEffect, useId, useImperativeHandle } from "react";

import Autocomplete from "../Autocomplete/Autocomplete.jsx";
import "./form.scss";
import { shouldSubmitOnKey } from "./formSubmit.js";

function joinClassNames(classMap) {
  return Object.entries(classMap)
    .filter(([, enabled]) => Boolean(enabled))
    .map(([className]) => className)
    .join(" ");
}

function Form({ fields = [], onSubmit, setIsValid }, ref) {
  // Unique per <Form> instance, so two forms on one page (or two fields sharing
  // a label) can never produce the same control id. React's useId wraps its
  // value in colons, which are legal in an id but break bare CSS/querySelector
  // selectors, so they are stripped — the remaining part is what makes the
  // value unique, so stripping them keeps ids distinct.
  const instanceId = `tw-form-${useId().replace(/:/g, "")}`;

  useEffect(() => {
    if (setIsValid) {
      setIsValid(isValidFn());
    }
  }, [fields, setIsValid]);

  const isValidFn = (printErrors) => {
    let valid = true;
    fields.forEach((field) => {
      if (field.show !== false && field.required && !field.value) {
        valid = false;
        if (printErrors) {
          console.error(`${field.label} is required`);
        }
      }
    });
    return valid;
  };

  useImperativeHandle(ref, () => {
    return { isValidFn };
  }, [isValidFn]);

  // Which keystrokes submit at all lives in `formSubmit.js`, on its own, because it
  // is the part with rules worth pinning — notably that Enter inside a textarea is
  // a newline. What stays here is what needs this component: whether there is an
  // `onSubmit` to call, and whether the form validates.
  const checkSubmit = (event) => {
    if (onSubmit && shouldSubmitOnKey(event)) {
      if (!isValidFn(true)) return;
      event.preventDefault();
      onSubmit();
    }
  };

  return (
    <>
      {fields.map((field, index) => {
        if (field.show === false) return null;

        if (field.type === "content") {
          return (
            <Fragment key={index}>
              {typeof field.content === "function"
                ? field.content()
                : field.content}
            </Fragment>
          );
        }

        const showRequiredStar = field.showRequiredStar ?? field.required;
        // A caller may pin its own id; otherwise one is derived from this
        // instance's prefix. Never derived from field.name/label — those are not
        // guaranteed unique within a document.
        const controlId = field.id || `${instanceId}-${index}`;
        return (
          <div
            className={joinClassNames({
              "label-row": true,
              "vertical-align": field.type !== "textarea",
              [field.className]: field.className,
              required: showRequiredStar,
            })}
            key={index}
          >
            {!field.hideLabel && (
              <label htmlFor={controlId}>
                {field.label}
                {showRequiredStar && <span className="required-star"> *</span>}
              </label>
            )}

            {field.type === "textarea" ? (
              <textarea
                id={controlId}
                value={field.value}
                className={joinClassNames({
                  empty: field.required && !field.value,
                  [field.className]: Boolean(field.className),
                })}
                onChange={field.onChange && field.onChange}
                onKeyDown={checkSubmit}
                placeholder={field.placeholder}
                rows={field.rows || 4}
                disabled={field.disabled}
              />
            ) : field.type === "select" ? (
              <select
                id={controlId}
                value={field.value}
                onChange={field.onChange && field.onChange}
                onKeyDown={checkSubmit}
                disabled={field.disabled}
                className={joinClassNames({
                  empty: field.required && !field.value,
                })}
              >
                {field.options?.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            ) : field.type === "autocomplete" ? (
              <Autocomplete
                id={controlId}
                value={field.value}
                initValue={field.initValue}
                onSelect={field.onSelect && field.onSelect}
                onChange={field.onChange && field.onChange}
                items={field.items}
                allowClear={field.allowClear}
                placeholder={field.placeholder}
                disabled={field.disabled}
                className={joinClassNames({
                  empty: field.required && !field.value,
                })}
                onKeyDown={checkSubmit}
              />
            ) : field.type === "checkbox" ? (
              <div className="content-checkbox">
                <input
                  id={controlId}
                  type="checkbox"
                  checked={field.value}
                  disabled={field.disabled}
                  className={joinClassNames({
                    empty: field.required && !field.value,
                  })}
                  onChange={field.onChange}
                />
              </div>
            ) : field.type === "date" ? (
              <input
                id={controlId}
                type="date"
                value={field.value}
                onChange={field.onChange}
                onKeyDown={checkSubmit}
                className={joinClassNames({
                  empty: field.required && !field.value,
                })}
                disabled={field.disabled}
              />
            ) : field.type === "datetime-local" ? (
              <input
                id={controlId}
                type="datetime-local"
                value={field.value}
                onChange={field.onChange}
                onKeyDown={checkSubmit}
                className={joinClassNames({
                  empty: field.required && !field.value,
                })}
                disabled={field.disabled}
              />
            ) : (
              <input
                id={controlId}
                type={field.type === "number" ? "number" : "text"}
                value={field.value}
                onChange={field.onChange && field.onChange}
                placeholder={field.placeholder}
                onKeyDown={checkSubmit}
                disabled={field.disabled}
                className={joinClassNames({
                  empty: field.required && !field.value,
                })}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

export default forwardRef(Form);
